import React, { useEffect, useState } from 'react';
import { Livraison, LivraisonRequest, createLivraison, getLivraisons, updateLivraison } from '../../services/apiLivraisons';
import { DataTable } from '../../components/base/DataTable';
import { parseToCurrency } from '../../context/panier-context';
import { Badge } from '../../components/base/Badge';
import Section from "../../components/base/Section";
import { Title } from "../../components/base/Title";
import {
	filtersStatutLivraison,
	getColorStatutLivraison,
	getStatutLivraison
} from "../../utils/utils";
import { DialogPreparerLivraison } from "../../components/DialogPreparerLivraison";
import { handleApiError } from 'utils/errorHandlers';
import { ApiLoading } from 'components/ApiLoading';
import { Button } from 'components/base/Button';
import { Input } from 'components/base/Input';
import { Dialog } from 'components/base/Dialog';
import { ApiError } from 'components/ApiError';
import { Option, Select } from 'components/base/Select';
import { getGroupesWithReferents, GroupesWithReferents } from 'services/apiUtilisateurs';
import { Checkbox } from 'components/base/Checkbox';
import AlertLivraisonMissing from "../../components/AlertLivraisonMissing";

export const SuiviLivraisons: React.FC = () => {
	const [livraisons, setLivraisons] = useState<Livraison[]>([]);
	const [loading, setLoading] = useState<{ state: boolean, type: 'general' | 'form' }>({ state: true, type: 'general' });
	const [selectedLivraisonId, setSelectedLivraisonId] = useState<number | null>(null);
	const [errors, setErrors] = useState<Record<string, string[]>>({});
	const [open, setOpen] = useState<string | null>(null);

	const emptyForm = {
		date_livraison: '',
		heure_livraison: '',
		adresse: '',
		groupe: 1,
		group_id: 1,
		referent: 1,
		notes: '',
		livree: false,
	}
	
	const [form, setForm] = useState<any>(emptyForm);

	const [groupOptions, setGroupOptions] = useState<Option[]>([])
	const [referentOptions, setReferentOptions] = useState<Option[]>([])
	const [groupesWithReferents, setGroupesWithReferents] = useState<GroupesWithReferents[]>([])

	useEffect(() => {
		const fetchLivraisons = async () => {
			try {
				const data = await getLivraisons();
				setLivraisons(data);
			} catch (error: any) {
				setErrors(handleApiError(error));
			} finally {
				setLoading({ state: false, type: 'general' });
			}
		};

		const fetchGroupes = async () => {
			try {
				const data = await getGroupesWithReferents();
				setGroupesWithReferents(data);
				setGroupOptions(data.map((currentGroupe) => {
					return { value: currentGroupe.id.toString(), label: currentGroupe.groupe }
				}));
			} catch (error: any) {
				setErrors(handleApiError(error));
			} finally {
				setLoading({ state: false, type: 'form' });
			}
		};
		fetchLivraisons();

		fetchGroupes();
	}, []);

	const getReferentOptions = (groupeId: number) => {
		return groupesWithReferents.find((currentGroupe) => currentGroupe.id === groupeId)?.referents.map((currentReferent) => {
			return { value: currentReferent.id.toString(), label: currentReferent.nom_complet }
		})
	}

	const getGroupeAdresse = (groupeId: number) => {
		return groupesWithReferents.find((currentGroupe) => currentGroupe.id === groupeId)?.adresse_par_defaut
	}

	if (errors && errors.general) {
		return <ApiLoading type="error">{errors.general[0]}</ApiLoading>;
	}

	if (loading.type === 'general' && loading.state) {
		return <ApiLoading type="loading">Chargement des livraisons...</ApiLoading>;
	}

	if (livraisons.length === 0) {
		return <ApiLoading type="no-data">Aucune livraison trouvée</ApiLoading>;
	}

	const handleOpen = (type: string, row: Livraison | null = null) => {
		setOpen(type);
		setSelectedLivraisonId(row?.id ?? null);
		if (type === 'edit' && row) {
			setForm({
				id: row.id,
				groupe: row.group_id,
				date_livraison: row.date_livraison?.split(' ')[0],
				heure_livraison: row.date_livraison?.split(' ')[1],
				referent: row.referent?.id.toString() ?? getReferentOptions(row.group_id)?.[0]?.value ?? '',
				notes: row.notes,
				livree: row.statut === 'LIVREE',
			});
			setReferentOptions(getReferentOptions(row.group_id) ?? []);
		} else if (type === 'create') {
			setReferentOptions(getReferentOptions(1) ?? []);
			setForm({
				...emptyForm,
				adresse: getGroupeAdresse(1),
				referent: getReferentOptions(1)?.[0]?.value ?? '',
			});
		}
	}


	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setForm({ ...form, [e.target.name]: e.target.value });
	}

	// Gestionnaire dédié au Select (reçoit une string, convertit en number)
	const handleGroupeChange = (value: string) => {
		setForm((prev: any) => ({
			...prev,
			groupe: Number(value),
		}));
		setReferentOptions(getReferentOptions(Number(value)) ?? []);
		setForm((prev: any) => ({
			...prev,
			adresse: getGroupeAdresse(Number(value)),
			referent: getReferentOptions(Number(value))?.[0]?.value ?? '',
		}));
	};

	const handleReferentChange = (value: string) => {
		setForm((prev: any) => ({
			...prev,
			referent: value,
		}));
	};

	const handleSubmitCreate = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();		

		try {
			const data = {
				...form,
				statut: 'PLANIFIEE',
				group_id: form.groupe,
				date_livraison: form.date_livraison + ' ' + form.heure_livraison,
			}
			const response = await createLivraison(data);
			setLivraisons([response, ...livraisons]);
			setOpen(null);
			setForm(emptyForm);
		} catch (error) {
			console.error('Erreur lors de l\'ajout de la livraison:', error);
			setErrors(handleApiError(error));
		} finally {
			setLoading({ state: false, type: 'form' });
		}
	}

	const handleSubmitUpdate = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		if (!selectedLivraisonId) return;


		try {
			const data = {
				...form,
				statut: form.livree ? 'LIVREE' : 'PLANIFIEE',
				group_id: form.groupe,
				date_livraison: form.date_livraison + ' ' + form.heure_livraison,
			}
			const response = await updateLivraison(selectedLivraisonId, data);
			setLivraisons(livraisons.map((livraison) => livraison.id === selectedLivraisonId ? response : livraison));

			setOpen(null);
			setForm(emptyForm);
		} catch (error) {
			console.error('Erreur lors de la mise à jour de la livraison:', error);
			setErrors(handleApiError(error));
		} finally {
			setLoading({ state: false, type: 'form' });
		}
	}

	const handleLivree = async (row: Livraison) => {
		try {
			const data = {
				statut: 'LIVREE',
				adresse: row.adresse,
				date_livraison: row.date_livraison,
				group_id: row.group_id,
				referent: row.referent.id,
				notes: row.notes,
			} as LivraisonRequest;
			const response = await updateLivraison(row.id, data);
			setLivraisons(livraisons.map((livraison) => livraison.id === row.id ? response : livraison));
		} catch (error) {
			console.error('Erreur lors de la mise à jour de la livraison:', error);
			setErrors(handleApiError(error));
		} finally {
			setLoading({ state: false, type: 'form' });
		}
	}

	return (
		<Section fullwidth>
			<DialogPreparerLivraison
				open={open === 'preparer'}
				onClose={() => setOpen(null)}
				livraisonId={selectedLivraisonId}
				livraisonNote={livraisons.find((livraison) => livraison.id === selectedLivraisonId)?.notes ?? null}
			/>

			<Dialog
				open={open === 'create' || open === 'edit'}
				onClose={() => setOpen(null)}
				title={open === 'create' ? 'Ajouter une livraison' : 'Modifier la livraison'}
				customBtn={true}
			>
				<form onSubmit={open === 'create' ? handleSubmitCreate : handleSubmitUpdate} className="flex flex-col gap-2">
					<Input
						type="date"
						name="date_livraison"
						value={form.date_livraison}
						onChange={handleChange}
						label="Date de livraison"
						width="100%"
						required
					/>
					<Input
						type="time"
						name="heure_livraison"
						value={form.heure_livraison}
						onChange={handleChange}
						label="Heure de livraison"
						width="100%"
						required
					/>
					<Select
						name="groupe"
						value={form.groupe}
						options={groupOptions}
						onChange={(e) => handleGroupeChange(e.target.value)}
						label="Groupe"
						width="100%"
						required
					/>
					<Select
						name="referent"
						value={form.referent}
						options={referentOptions}
						onChange={(e) => handleReferentChange(e.target.value)}
						label="Référent"
						width="100%"
						required
					/>
					<Input
						type="text"
						name="adresse"
						value={form.adresse}
						onChange={handleChange}
						label="Adresse"
						width="100%"
						required
					/>
					<Input
						type="textarea"
						rows={3}
						name="notes"
						value={form.notes}
						onChange={handleChange}
						label="Notes"
						width="100%"
					/>

					{open === 'edit' && (
						<Checkbox
							label="Livraison livrée ?"
							checked={form.livree}
							color='bg-primary'
							onChange={(checked) => setForm((prev: any) => ({ ...prev, livree: checked }))}
						/>
					)}

					{errors.form && (
						<ApiError>
							{errors.form[0]}
						</ApiError>
					)}


					<div className='flex justify-end gap-2 mt-6'>
						<Button type="button" onClick={() => setOpen(null)} variant='text'>Annuler</Button>
						<Button type="submit" variant="primary">{loading.type === 'form' && loading.state ? 'Veuillez patienter...' : 'Confirmer'}</Button>
					</div>
				</form>
			</Dialog>

			<div className="flex justify-between items-center gap-2 mb-6">
				<Title type="h2" className="mb-4">Suivi des livraisons</Title>
				<Button variant='primary' onClick={() => handleOpen('create')}>Ajouter</Button>
			</div>

			<AlertLivraisonMissing forAdmin={true} />

			<DataTable
				columns={[
					{ key: 'group_id', label: 'Groupe Id', hidden: true },
					{ key: 'date_livraison', label: 'Date de livraison', hidden: true },
					{ key: 'id', label: 'N° Livraison', sortable: true, render: (value) => `#${value}` },
					{ key: 'date_livraison_display', label: 'Date de livraison', },
					{ key: 'groupe_nom', label: 'Groupe', },
					{ key: 'referent_nom', label: 'Référent', },
					{
						key: 'statut', label: 'Statut',
						render: (value) => <Badge bgColor={getColorStatutLivraison(value)}>{getStatutLivraison(value)}</Badge>,
						filterOptions: filtersStatutLivraison()
					},
					{ key: 'nb_paniers', label: 'Nb de panier' },
					{ key: 'total', label: 'Total', render: (value) => parseToCurrency(value) },
				]}
				data={livraisons}
				actions={[
					{ label: 'Commande livrée ✓', buttonVariant: "success", onClick: (row) => { handleLivree(row); }, hidden: (row) => row.statut === 'LIVREE'},
					{ label: 'Préparer', buttonVariant: "primary", onClick: (row) => { handleOpen('preparer', row); } },
					{ label: 'Modifier', buttonVariant: "primary", onClick: (row) => { handleOpen('edit', row); } },
				]}
				actionWidth='w-[420px]'
			/>
		</Section>
	);
};